import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys, SEARCH_SORTS, type SearchSort } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { trackSearch } from '@/lib/analytics';
import { publicApi } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { useDebounced } from '@/lib/use-debounced';
import { clearRecentSearches, readRecentSearches, rememberSearch } from '@/lib/recent-searches';
import { SearchResults } from '@/components/search/SearchResults';
import { SearchFilters } from '@/components/search/SearchFilters';
import { Button, Input } from '@/components/ui';
import { PageHeader, Section } from './page-parts';

/**
 * `/:locale/search` — PRD §15.
 *
 * **The URL is the state.** Query, filters, sort and page all live in the query string,
 * which is what makes a result list shareable, bookmarkable and survivable by the back
 * button — the Phase 10 DoD asks for exactly that, and it is also why this page keeps no
 * duplicate state of its own beyond the text currently being typed.
 *
 * The typed value is debounced by 300 ms before it reaches the URL and the query, so a
 * reader sees every keystroke immediately while the network sees one request per pause.
 */
export default function SearchPage() {
  const { locale, t } = useLocale();
  const [params, setParams] = useSearchParams();

  const query = params.get('q') ?? '';
  const sort = (params.get('sort') as SearchSort | null) ?? 'relevance';
  const page = Number(params.get('page') ?? '1');
  const filters = {
    category: params.get('category') ?? undefined,
    district: params.get('district') ?? undefined,
    author: params.get('author') ?? undefined,
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
  };

  const [typed, setTyped] = useState(query);
  const debounced = useDebounced(typed, 300);
  const [recent, setRecent] = useState<string[]>([]);

  // Read after mount: `localStorage` does not exist during SSR, and an empty first
  // render is what keeps hydration honest.
  useEffect(() => setRecent(readRecentSearches()), []);

  // The debounced value drives the URL; `replace` so typing does not fill the history
  // with one entry per pause, while the *first* search of a session stays a back step.
  useEffect(() => {
    if (debounced === query) return;
    const next = new URLSearchParams(params);
    if (debounced) next.set('q', debounced);
    else next.delete('q');
    next.delete('page');
    setParams(next, { replace: Boolean(query) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `params` would loop
  }, [debounced]);

  // Only a query that produced results is worth offering back to the reader.
  const enabled = query.trim().length > 0;
  const { data, isPending, isError, refetch, isFetching } = useQuery({
    queryKey: queryKeys.search(locale, query, page, sort, filters),
    queryFn: () => publicApi.search({ locale, q: query, sort, page, ...filters }),
    enabled,
    staleTime: STALE.search,
  });

  useEffect(() => {
    if (data && data.total > 0) setRecent(rememberSearch(query));
  }, [data, query]);

  /**
   * The query as a reader typed it, with what it found (Phase 16).
   *
   * Only on page one: paging through results is one search, not five, and counting each
   * page would make a much-read query look like a much-*repeated* one. The API logs its
   * own copy of every query it is asked; this is the half that came from a person.
   */
  useEffect(() => {
    if (!data || page !== 1 || !enabled) return;
    trackSearch(`/${locale}/search`, query, data.total);
  }, [data, query, page, enabled, locale]);

  const update = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    // Any change to the question invalidates the page number.
    if (!('page' in changes)) next.delete('page');
    setParams(next);
  };

  return (
    <>
      <PageHeader
        title={t('search.title')}
        subtitle={data ? t('search.resultCount', { count: data.total, query }) : undefined}
      />

      <Section>
        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <div className="flex flex-col gap-5">
            <Input
              label={t('search.title')}
              labelHidden
              type="search"
              autoFocus
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={t('search.placeholder')}
            />

            <SearchFilters
              filters={filters}
              sort={sort}
              sorts={SEARCH_SORTS}
              onChange={update}
              disabled={!enabled}
            />

            {recent.length > 0 && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-xs font-extrabold uppercase tracking-wide text-ink">
                    {t('search.recent')}
                  </h2>
                  <Button
                    variant="link"
                    size="sm"
                    className="px-0"
                    onClick={() => {
                      clearRecentSearches();
                      setRecent([]);
                    }}
                  >
                    {t('search.clearRecent')}
                  </Button>
                </div>
                <ul className="flex flex-wrap gap-2">
                  {recent.map((item) => (
                    <li key={item}>
                      <button
                        type="button"
                        onClick={() => setTyped(item)}
                        className="rounded-md border border-line px-3 py-1.5 text-xs font-semibold text-body transition hover:border-red hover:text-red"
                      >
                        {item}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <SearchResults
            query={query}
            enabled={enabled}
            hits={data?.hits ?? []}
            total={data?.total ?? 0}
            page={page}
            perPage={data?.perPage ?? 20}
            isPending={enabled && (isPending || isFetching)}
            isError={isError}
            onRetry={() => void refetch()}
            onPage={(next) => update({ page: String(next) })}
          />
        </div>
      </Section>
    </>
  );
}
